import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Upload, Search, Folder, Image, Video, Music,
  Grid3x3, List, Trash2, HardDrive, Loader2
} from 'lucide-react';
import uploadService from '../../../services/uploadService';
import assetService from '../../../services/assetService';

export default function AssetLibraryModal({
  isOpen,
  onClose,
  onSelect,
  filterType = null
}) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [uploading, setUploading] = useState(false);
  const [activeType, setActiveType] = useState(filterType || 'all');
  const fileInputRef = useRef(null);

  // Fetch assets when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAssets();
    }
  }, [isOpen]);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await assetService.listAssets();
      setAssets(response.data || []);
    } catch (err) {
      console.error('Failed to load assets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);

    for (const file of Array.from(files)) {
      try {
        // Step 1: Upload to S3
        const uploadResult = await uploadService.uploadFile(file);
        if (!uploadResult || !uploadResult.url) {
          throw new Error('No URL returned from upload');
        }

        // Step 2: Detect type from MIME
        const type = file.type.startsWith('image/') ? 'image'
          : file.type.startsWith('video/') ? 'video'
          : file.type.startsWith('audio/') ? 'audio' : 'image';

        // Step 3: Create asset record
        const result = await assetService.createAsset({
          name: file.name.replace(/\.[^/.]+$/, ''),
          type,
          file_url: uploadResult.url,
          file_size: file.size,
          mimeType: file.type,
          folder: selectedFolder === 'all' ? null : selectedFolder,
        });

        if (result.data) {
          setAssets(prev => [result.data, ...prev]);
        }
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSelectAsset = (asset) => {
    onSelect?.(asset);
    onClose();
  };

  const handleDeleteAsset = async (assetId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this asset?')) return;
    try {
      await assetService.deleteAsset(assetId);
      setAssets(prev => prev.filter(a => (a.id || a._id) !== assetId));
    } catch (err) {
      console.error('Failed to delete asset:', err);
    }
  };

  // Client-side filtering
  const filteredAssets = assets.filter(asset => {
    if (activeType !== 'all' && asset.type !== activeType) return false;
    if (selectedFolder !== 'all' && asset.folder !== selectedFolder) return false;
    if (searchQuery && !asset.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const folders = [...new Set(assets.map(a => a.folder).filter(Boolean))];

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(0)} KB` : `${mb.toFixed(1)} MB`;
  };

  const getAcceptTypes = () => {
    if (filterType === 'image') return 'image/*';
    if (filterType === 'video') return 'video/*';
    if (filterType === 'audio') return 'audio/*';
    return 'image/*,video/*,audio/*';
  };

  const typeTabs = [
    { id: 'all', label: 'All', icon: null },
    { id: 'image', label: 'Images', icon: Image },
    { id: 'video', label: 'Videos', icon: Video },
    { id: 'audio', label: 'Audio', icon: Music },
  ];

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/80" onClick={onClose} />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-5xl max-h-[85vh] bg-[#001233] rounded-xl border border-white/10 flex flex-col"
        >
          {/* Header */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white">Asset Library</h2>
              <p className="text-xs text-gray-400">Browse and manage your media files</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Toolbar */}
          <div className="p-4 border-b border-white/10 space-y-3">
            {/* Search & Upload */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  placeholder="Search assets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors"
                />
              </div>
              <label className="cursor-pointer">
                <div className="flex items-center gap-2 px-4 py-2 bg-[#6a1fbf] hover:bg-[#7a2fcf] rounded-lg text-white text-sm font-medium transition-colors">
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  {uploading ? 'Uploading...' : 'Upload'}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={getAcceptTypes()}
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>

            {/* Filters + View Toggle */}
            <div className="flex items-center justify-between">
              {/* Type Tabs */}
              <div className="flex bg-white/5 rounded-lg p-1">
                {typeTabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveType(tab.id)}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors flex items-center gap-1.5 ${
                        activeType === tab.id
                          ? 'bg-white/10 text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {Icon && <Icon className="w-3.5 h-3.5" />}
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* View Toggle */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'grid' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'list' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex overflow-hidden">
            {/* Sidebar - Folders */}
            <div className="w-48 border-r border-white/10 p-3 overflow-y-auto">
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Folders</div>
              <div className="space-y-1">
                <button
                  onClick={() => setSelectedFolder('all')}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${
                    selectedFolder === 'all'
                      ? 'bg-[#6a1fbf]/30 text-white'
                      : 'text-gray-400 hover:bg-white/5'
                  }`}
                >
                  <Folder className="w-3.5 h-3.5" />
                  All Assets
                </button>
                {folders.map(folder => (
                  <button
                    key={folder}
                    onClick={() => setSelectedFolder(folder)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${
                      selectedFolder === folder
                        ? 'bg-[#6a1fbf]/30 text-white'
                        : 'text-gray-400 hover:bg-white/5'
                    }`}
                  >
                    <Folder className="w-3.5 h-3.5" />
                    {folder}
                  </button>
                ))}
              </div>
            </div>

            {/* Assets Grid/List */}
            <div className="flex-1 overflow-y-auto">
              {uploading && (
                <div className="p-4 bg-[#6a1fbf]/20 border-b border-white/10 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                  <span className="text-sm text-white">Uploading files...</span>
                </div>
              )}

              {loading ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <Loader2 className="w-8 h-8 animate-spin mb-3" />
                  <p className="text-sm">Loading assets...</p>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-4 gap-3 p-4">
                  {filteredAssets.map(asset => (
                    <motion.div
                      key={asset.id || asset._id}
                      layout
                      onClick={() => handleSelectAsset(asset)}
                      className="group relative aspect-square rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border border-white/10 cursor-pointer transition-colors"
                    >
                      {/* Preview */}
                      {asset.type === 'image' && (
                        <img
                          src={asset.file_url}
                          alt={asset.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {asset.type === 'video' && (
                        <div className="flex items-center justify-center h-full">
                          <Video className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                      {asset.type === 'audio' && (
                        <div className="flex items-center justify-center h-full">
                          <Music className="w-12 h-12 text-gray-400" />
                        </div>
                      )}

                      {/* Source badge */}
                      {asset.source === 'element' && (
                        <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-blue-500/80 rounded text-[9px] text-white font-medium">
                          Element
                        </div>
                      )}

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-2">
                          <div className="text-xs text-white font-medium truncate">{asset.name}</div>
                          <div className="text-[10px] text-gray-400">{formatFileSize(asset.file_size)}</div>
                        </div>
                        {asset.source !== 'element' && (
                          <button
                            onClick={(e) => handleDeleteAsset(asset.id || asset._id, e)}
                            className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded transition-colors"
                          >
                            <Trash2 className="w-3 h-3 text-white" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="p-4 space-y-1">
                  {filteredAssets.map(asset => (
                    <div
                      key={asset.id || asset._id}
                      onClick={() => handleSelectAsset(asset)}
                      className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        {asset.type === 'image' && <Image className="w-4 h-4 text-blue-400" />}
                        {asset.type === 'video' && <Video className="w-4 h-4 text-purple-400" />}
                        {asset.type === 'audio' && <Music className="w-4 h-4 text-green-400" />}
                        <div>
                          <div className="text-sm text-white flex items-center gap-2">
                            {asset.name}
                            {asset.source === 'element' && (
                              <span className="px-1.5 py-0.5 bg-blue-500/30 rounded text-[9px] text-blue-300 font-medium">Element</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400">{formatFileSize(asset.file_size)}</div>
                        </div>
                      </div>
                      {asset.source !== 'element' && (
                        <button
                          onClick={(e) => handleDeleteAsset(asset.id || asset._id, e)}
                          className="p-1.5 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {filteredAssets.length === 0 && !uploading && !loading && (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <HardDrive className="w-12 h-12 mb-3 opacity-50" />
                  <p className="text-sm">No assets found</p>
                  <p className="text-xs mt-1">Upload some files to get started</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
